import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { toast } from '../store/useToasts';
import type { GuacamoleConsoleToken, RangeDetail, RangeStatus, RangeSummary } from '../api/types';

/** Poll cadence while a range is mid-transition (the wait users watch). */
export const TRANSITIONAL_POLL_MS = 10_000;
/** Poll cadence once every range is steady — light background freshness. */
export const STEADY_POLL_MS = 60_000;

/** Statuses that are still moving and warrant a fast list refresh (F3). */
const TRANSITIONAL_STATUSES: ReadonlySet<RangeStatus> = new Set<RangeStatus>([
  'pending',
  'provisioning',
  'destroying',
  'attacking',
]);

/**
 * F3 — status-aware refetch cadence for the ranges list. Polls fast (10s)
 * while any range is transitional so "Deploying…" flips to "Open console" on
 * its own, and backs off to 60s once everything is steady.
 */
export function rangesRefetchInterval(
  data: ReadonlyArray<RangeSummary> | undefined,
): number {
  const anyTransitional = (data ?? []).some((r) => TRANSITIONAL_STATUSES.has(r.status));
  return anyTransitional ? TRANSITIONAL_POLL_MS : STEADY_POLL_MS;
}

/**
 * Range query hooks — backed by `services/deploy/handler.py` +
 * `services/monitor/handler.py`. Active ranges poll for health on a 30s
 * cadence (matches the queryClient default staleTime of 30s).
 */

export const rangeKeys = {
  all: ['ranges'] as const,
  list: () => [...rangeKeys.all, 'list'] as const,
  detail: (id: string) => [...rangeKeys.all, 'detail', id] as const,
  guacamoleToken: (rangeId: string, vmHostname: string) =>
    [...rangeKeys.all, 'guacamole-token', rangeId, vmHostname] as const,
};

export function useRanges() {
  return useQuery<ReadonlyArray<RangeSummary>>({
    queryKey: rangeKeys.list(),
    queryFn: async ({ signal }) => {
      const resp = await apiFetch<unknown>('/ranges', { method: 'GET', signal });
      if (Array.isArray(resp)) return resp as ReadonlyArray<RangeSummary>;
      const items = (resp as { items?: ReadonlyArray<RangeSummary> })?.items;
      return items ?? [];
    },
    // F3: auto-refresh while any range is transitional so the list flips
    // "Deploying…" → "Open console" without a manual reload.
    refetchInterval: (query) => rangesRefetchInterval(query.state.data),
  });
}

export function useRange(id: string | undefined) {
  return useQuery<RangeDetail>({
    queryKey: rangeKeys.detail(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: 30_000,
    queryFn: async ({ signal }) =>
      apiFetch<RangeDetail>('/monitor/ranges/{rangeId}/health', {
        method: 'GET',
        pathParams: { rangeId: id ?? '' },
        signal,
      }),
  });
}

/**
 * Mint a short-lived Guacamole HMAC-JSON token for a specific VM in a range.
 *
 * Operator decision 2026-05-25: Apache Guacamole is Forge's console layer
 * (replaces DCV). Token format documented at
 * https://guacamole.apache.org/doc/gug/json-auth.html - HMAC-SHA256-
 * signed JSON listing which connection(s) the bearer can open. Backend
 * resolves VM private IP + protocol (RDP/SSH) + per-trainee credentials.
 *
 * The shared Guacamole gateway (one ASG in platform VPC) accepts ANY
 * signed token because the HMAC secret is shared across all gateway
 * replicas. NLB picks which replica handles this trainee.
 */
export function useGuacamoleToken(
  rangeId: string | undefined,
  vmHostname: string | undefined,
  enabled: boolean,
) {
  return useQuery<GuacamoleConsoleToken>({
    queryKey: rangeKeys.guacamoleToken(rangeId ?? '', vmHostname ?? ''),
    enabled: enabled && Boolean(rangeId) && Boolean(vmHostname),
    staleTime: 0,
    gcTime: 0,
    queryFn: async ({ signal }) =>
      // The deployed API exposes the unified grantConsoleAccess route at
      // /console (it dispatches to the guacamole token — identical response).
      // /guacamole is declared in the handler but was never registered in API
      // Gateway, so calling it 404s and the token request hangs forever.
      apiFetch<GuacamoleConsoleToken>('/ranges/{rangeId}/vms/{vmName}/console', {
        method: 'POST',
        pathParams: { rangeId: rangeId ?? '', vmName: vmHostname ?? '' },
        body: {},
        signal,
      }),
  });
}

/**
 * Instructor-triggered attack. POSTs to the attack service's startPhase route,
 * which runs the phase's attack against the range's web target (generating the
 * telemetry the Wazuh rules detect). phaseId may be a phase id ('webshell'),
 * a 1-based number, or 'all' for the full chain.
 */
export function useStartPhase() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { rangeId: string; phaseId: string }>({
    mutationFn: async ({ rangeId, phaseId }) =>
      apiFetch<unknown>('/ranges/{rangeId}/phases/{phaseId}/start', {
        method: 'POST',
        pathParams: { rangeId, phaseId },
        body: {},
      }),
    onSuccess: (_d, { rangeId, phaseId }) => {
      toast.success('Attack phase started', `Phase "${phaseId}" is running on ${rangeId}.`);
      qc.invalidateQueries({ queryKey: rangeKeys.detail(rangeId) });
    },
    meta: { errorTitle: 'Could not start phase' },
  });
}

/** DS-9 — hibernate a range (stop its compute; keeps EBS). */
export function useHibernateRange() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { rangeId: string }>({
    mutationFn: async ({ rangeId }) =>
      apiFetch<unknown>('/ranges/{rangeId}/hibernate', {
        method: 'POST',
        pathParams: { rangeId },
        body: {},
      }),
    onSuccess: (_d, { rangeId }) => {
      toast.success('Range hibernating', `${rangeId} — compute is stopping; state is kept.`);
      qc.invalidateQueries({ queryKey: rangeKeys.detail(rangeId) });
      qc.invalidateQueries({ queryKey: rangeKeys.list() });
    },
    meta: { errorTitle: 'Hibernate failed' },
  });
}

/** DS-9 — resume a hibernated range (start its stopped instances). */
export function useResumeRange() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { rangeId: string }>({
    mutationFn: async ({ rangeId }) =>
      apiFetch<unknown>('/ranges/{rangeId}/resume', {
        method: 'POST',
        pathParams: { rangeId },
        body: {},
      }),
    onSuccess: (_d, { rangeId }) => {
      toast.success('Range resuming', `${rangeId} — VMs are starting back up (~1-2 min).`);
      qc.invalidateQueries({ queryKey: rangeKeys.detail(rangeId) });
      qc.invalidateQueries({ queryKey: rangeKeys.list() });
    },
    meta: { errorTitle: 'Resume failed' },
  });
}

export function useDestroyRange() {
  const qc = useQueryClient();
  return useMutation<void, Error, { rangeId: string }>({
    mutationFn: async ({ rangeId }) => {
      await apiFetch<void>('/ranges/{rangeId}', {
        method: 'DELETE',
        pathParams: { rangeId },
        body: { confirm: 'DELETE' },
      });
    },
    onSuccess: (_d, { rangeId }) => {
      toast.success('Range destroyed', `${rangeId} has been torn down.`);
      qc.invalidateQueries({ queryKey: rangeKeys.all });
    },
    meta: { errorTitle: 'Destroy failed' },
  });
}
